import {
  type Epic,
  type Feature,
  type Goal,
  type Initiative,
  Project,
  type Requirement,
  Workflow,
  WorkflowStatus,
} from 'components/framework/models';
import { lowerFirst } from 'lodash';
import RecordAction from '../RecordAction';
import type ApplicationModel from 'components/framework/ApplicationModel';

type RecordType = Epic | Feature | Requirement | Goal | Initiative;

export class UpdateStatusAction extends RecordAction<RecordType> {
  recordTypes = ['Epic', 'Feature', 'Requirement', 'Goal', 'Initiative'];

  get title() {
    return 'Update status';
  }

  get hasSubmenu() {
    return true;
  }

  showForRecord(record: ApplicationModel): record is RecordType {
    if (!super.showForRecord(record)) {
      return false;
    }
    if (!this.selectedRecords.every(r => r.typename === record.typename)) {
      return false;
    }
    if (window.currentProject?.isTeam) {
      // Goals and Initiatives don't have a team property
      if (record.typename === 'Goal' || record.typename === 'Initiative') {
        return false;
      }
      return record.team?.id === window.currentProject?.id;
    }
    return true;
  }

  async prepareSubmenuItems() {
    const currentProjectId = window.currentProject?.id;

    if (!currentProjectId) {
      return [];
    }

    const recordType = lowerFirst(this.selectedRecords[0].internalTypename);
    const recordWorkflow = `${recordType}Workflow`;
    const project = await Project.select('id')
      .merge({
        [recordWorkflow]: Workflow.select('id').merge({
          workflowStatuses: WorkflowStatus.select('id', 'name'),
        }),
      })
      .find(currentProjectId);

    const workflowStatuses = project[recordWorkflow]?.workflowStatuses;

    if (workflowStatuses && workflowStatuses.length) {
      return workflowStatuses.map(status => ({
        label: status.name,
        data: { id: status.id },
      }));
    }

    return [];
  }

  async runForRecords(records, { id }) {
    if (!id) return;

    const statusType = window.currentProject?.isTeam
      ? 'teamWorkflowStatus'
      : 'workflowStatus';

    await Promise.all(
      records.map(record =>
        record.update(
          {
            [statusType]: new WorkflowStatus({ id }),
          },
          { args: { __reactivelyUpdate: true } }
        )
      )
    );
  }
}
